/* eslint-disable camelcase */
import { Button, useForm } from '@rocketmakers/armstrong';
import * as React from 'react';
import { DeviceType, RuleActionType, RuleCategory } from '../../../../../backend/shared/api-clients/_api/config';
import { FilterSelect } from '../../../../shared/components/filter-select';
import { useRoutePaths } from '../../../../shared/context/paths';
import { enumToArray, generateSelectOptions } from '../../../../shared/helpers/enumHelpers';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { InputValidator } from '../../../../shared/hooks/ValidationTypes';
import { BackButton } from '../../components/buttons/back-button';
import { Spinner } from '../../components/spinner/spinner';
import { useBeeConfig } from '../../hooks/beeConfig';
import { routePaths } from '../../routes';

export const TOTAL_CLAMPS = 3;

const SUPPORTED_DEVICE_TYPES = [
  DeviceType.EASTRON_SDM630MCT_4L,
  DeviceType.EASTRON_SDM630MCT_MV,
  DeviceType.SCHNEIDER_IEM3455,
];

interface ICTModelForm {
  model: DeviceType.SCHNEIDER_IEM3455 | DeviceType.EASTRON_SDM630MCT_4L | DeviceType.EASTRON_SDM630MCT_MV;
}

export const validators: InputValidator<ICTModelForm>[] = [
  {
    key: 'model',
    validators: [
      {
        validate: value =>
          enumToArray(
            DeviceType,
            false,
            DeviceType.SCHNEIDER_IEM3455
            // DeviceType.EASTRON_SDM630MCT_4L,  // Disabled as they are unsupported
            // DeviceType.EASTRON_SDM630MCT_MV  // Disabled as they are unsupported
          ).includes(value),
        message: 'Must be a valid meter',
      },
    ],
  },
];

export const CTModelView: React.FunctionComponent = () => {
  const { push } = useRoutePaths();
  const { devices, getDevicesByCategory, loading } = useBeeConfig();

  const initialState: ICTModelForm = React.useMemo(() => {
    const [deviceRule] = getDevicesByCategory(RuleCategory.ELECTRICITY);
    if (deviceRule) {
      return {
        model: deviceRule.deviceType as
          | DeviceType.SCHNEIDER_IEM3455
          | DeviceType.EASTRON_SDM630MCT_4L
          | DeviceType.EASTRON_SDM630MCT_MV,
      } as ICTModelForm;
    }
    return {
      model: null,
    };
  }, [devices]);

  const { DataForm, bind, dataBinder } = useForm(initialState);
  const { setTouched, formIsValid, validationErrors } = useValidatedForm<ICTModelForm>(validators, dataBinder);

  const handleContinue = () => {
    const [deviceRule] = getDevicesByCategory(RuleCategory.ELECTRICITY).filter(d =>
      SUPPORTED_DEVICE_TYPES.includes(d.deviceType)
    );
    const device = {
      deviceType: dataBinder.getKeyValue('model'),
      params: {
        category: RuleCategory.ELECTRICITY,
        action_type: RuleActionType.MONITORING,
        address: 6,
        clamp_1_circuit_label: null,
        clamp_2_circuit_label: null,
        clamp_3_circuit_label: null,
        clamp_1_circuit_type: null,
        clamp_2_circuit_type: null,
        clamp_3_circuit_type: null,
        non_metered_circuits: null,
        channel: 'C4',
        ...deviceRule?.params,
      },
      ...deviceRule,
    };
    push(routePaths.currentTransformerMeters.configuration, {}, device);
  };

  const options = generateSelectOptions(
    DeviceType,
    false,
    DeviceType.SCHNEIDER_IEM3455,
    DeviceType.EASTRON_SDM630MCT_4L,
    DeviceType.EASTRON_SDM630MCT_MV
  );

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />
      <h1 data-qa="view-title">Current Transformer Meter</h1>

      <div>
        <FilterSelect
          {...bind.select('model')}
          data-qa="ct-model"
          optionLabel={'What is the make and model of CT meter?'}
          options={options}
          onBlur={setTouched}
        />
      </div>

      <div className="row">
        <Button
          className="continue-button btn-full btn-large"
          disabled={formIsValid === false}
          onClick={handleContinue}
          data-qa="continue-button"
        >
          Continue
        </Button>
      </div>
    </DataForm>
  );
};
